import { useEffect, useRef, useState } from 'react';
import {
  Table, Card, Tag, Typography, Button, Modal, Form,
  Input, Select, Popconfirm, message, Spin, Tooltip,
} from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  listMainlineDesignDocs,
  createMainlineDesignDoc,
  updateMainlineDesignDoc,
  deleteMainlineDesignDoc,
} from '@/api/requirements';
import { parseFeishuTitles } from '@/api/feishu';

interface DesignDoc {
  id: number;
  file_number: number;
  file_description: string;
  features: string;
  requirement_attribute: string;
  documents: string[] | { url: string; title?: string }[];
  user_version: string;
  remarks: string;
  updated_by: string;
  username: string;
  updated_at: string;
}

const FEATURES_COLOR: Record<string, string> = {
  MCU: 'blue',
  SOC: 'green',
  'MCU&SOC': 'purple',
};

const renderDocuments = (
  docs: DesignDoc['documents'],
  titlesMap: Record<string, string>,
  loadingUrls: Set<string>,
) => {
  if (!docs?.length) return <span style={{ color: '#999' }}>-</span>;
  return docs.map((d, i) => {
    const url = typeof d === 'string' ? d : d.url;
    const isFeishu = url.includes('feishu.cn/');
    const label = isFeishu
      ? (titlesMap[url] ?? (loadingUrls.has(url) ? <Spin size="small" /> : `文档${i + 1}`))
      : `文档${i + 1}`;
    return (
      <Typography.Link key={i} href={url} target="_blank" style={{ display: 'block' }}>
        {label}
      </Typography.Link>
    );
  });
};

const docsToText = (docs: DesignDoc['documents']): string =>
  docs?.map((d) => (typeof d === 'string' ? d : d.url)).join('\n') ?? '';

const textToDocs = (text: string): { url: string }[] =>
  text.split('\n').map((s) => s.trim()).filter(Boolean).map((url) => ({ url }));

const MainlineDesignDocs = () => {
  const [data, setData] = useState<DesignDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState<DesignDoc | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [feishuTitlesMap, setFeishuTitlesMap] = useState<Record<string, string>>({});
  const [loadingUrls, setLoadingUrls] = useState<Set<string>>(new Set());
  const requestedUrlsRef = useRef<Set<string>>(new Set());

  const fetchData = () => {
    setLoading(true);
    listMainlineDesignDocs()
      .then((res: any) => {
        const list = Array.isArray(res) ? res : res.data ?? [];
        setData(list);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    const urls: string[] = [];
    for (const doc of data) {
      for (const d of doc.documents ?? []) {
        const url = typeof d === 'string' ? d : d.url;
        if (url.includes('feishu.cn/') && !requestedUrlsRef.current.has(url)) {
          urls.push(url);
        }
      }
    }
    if (!urls.length) return;
    urls.forEach((u) => requestedUrlsRef.current.add(u));
    setLoadingUrls((prev) => new Set([...prev, ...urls]));
    parseFeishuTitles(urls).then(({ results }) => {
      setFeishuTitlesMap((prev) => {
        const next = { ...prev };
        for (const { url, title } of results) {
          if (title) next[url] = title;
        }
        return next;
      });
      setLoadingUrls((prev) => {
        const next = new Set(prev);
        urls.forEach((u) => next.delete(u));
        return next;
      });
    });
  }, [data]);

  const openCreate = () => {
    setEditingDoc(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record: DesignDoc) => {
    setEditingDoc(record);
    form.setFieldsValue({
      file_description: record.file_description,
      features: record.features,
      requirement_attribute: record.requirement_attribute,
      user_version: record.user_version,
      documents: docsToText(record.documents),
      remarks: record.remarks,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const payload = {
      ...values,
      documents: textToDocs(values.documents ?? ''),
    };
    setSubmitting(true);
    try {
      if (editingDoc) {
        await updateMainlineDesignDoc(editingDoc.id, payload);
        message.success('更新成功');
      } else {
        await createMainlineDesignDoc(payload);
        message.success('新增成功');
      }
      // 清除本次提交的 URL，fetchData 后 useEffect 会重新拉取标题
      const submittedUrls = (payload.documents as { url: string }[]).map((d) => d.url);
      submittedUrls.forEach((u) => requestedUrlsRef.current.delete(u));
      setModalOpen(false);
      fetchData();
    } catch {
      message.error(editingDoc ? '更新失败' : '新增失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteMainlineDesignDoc(id);
      message.success('删除成功');
      fetchData();
    } catch {
      message.error('删除失败');
    }
  };

  // 列宽调试区 —— 只改这里的数字即可
  const COL_WIDTHS = {
    文件编号:  80,
    文件描述:  200,
    Features:  110,
    需求属性:  90,
    文档:      200,
    版本:      70,
    备注:      300,
    更新人:    90,
    更新时间:  100,
    操作:      80,
  };

  const columns: ColumnsType<DesignDoc> = [
    { title: '文件编号', dataIndex: 'file_number', key: 'file_number', width: COL_WIDTHS.文件编号 },
    { title: '文件描述', dataIndex: 'file_description', key: 'file_description', width: COL_WIDTHS.文件描述 },
    {
      title: 'Features',
      dataIndex: 'features',
      key: 'features',
      width: COL_WIDTHS.Features,
      render: (v: string) => <Tag color={FEATURES_COLOR[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: '需求属性',
      dataIndex: 'requirement_attribute',
      key: 'requirement_attribute',
      width: COL_WIDTHS.需求属性,
      render: (attr: string) => <Tag color={attr === '必选' ? 'red' : 'default'}>{attr}</Tag>,
    },
    {
      title: '文档',
      dataIndex: 'documents',
      key: 'documents',
      width: COL_WIDTHS.文档,
      render: (docs: DesignDoc['documents']) => renderDocuments(docs, feishuTitlesMap, loadingUrls),
    },
    { title: '版本', dataIndex: 'user_version', key: 'user_version', width: COL_WIDTHS.版本 },
    {
      title: '备注',
      dataIndex: 'remarks',
      key: 'remarks',
      width: COL_WIDTHS.备注,
      render: (v: string) => (
        <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{v || '-'}</span>
      ),
    },
    { title: '更新人', dataIndex: 'username', key: 'username', width: COL_WIDTHS.更新人 },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: COL_WIDTHS.更新时间,
      render: (v: string) => v ? new Date(v).toLocaleDateString('zh-CN') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: COL_WIDTHS.操作,
      fixed: 'right',
      render: (_, record) => (
        <>
          <Tooltip title="编辑">
            <Button size="small" type="link" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Popconfirm title="确认删除该文档？" onConfirm={() => handleDelete(record.id)}>
            <Tooltip title="删除">
              <Button size="small" type="link" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <Card
      title="主线设计文档"
      extra={<Button type="primary" onClick={openCreate}>新增设计文档</Button>}
    >
      <Table
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={false}
        scroll={{ x: Object.values(COL_WIDTHS).reduce((a, b) => a + b, 0) }}
      />

      <Modal
        title={editingDoc ? '编辑设计文档' : '新增设计文档'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        confirmLoading={submitting}
        width={600}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="file_description" label="文件描述" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="features" label="Features" rules={[{ required: true }]}>
            <Select options={['MCU', 'SOC', 'MCU&SOC'].map((v) => ({ value: v, label: v }))} />
          </Form.Item>
          <Form.Item name="requirement_attribute" label="需求属性" rules={[{ required: true }]}>
            <Select options={['必选', '可选'].map((v) => ({ value: v, label: v }))} />
          </Form.Item>
          <Form.Item name="user_version" label="版本">
            <Input />
          </Form.Item>
          <Form.Item name="documents" label="文档链接（每行一个 URL）">
            <Input.TextArea rows={4} placeholder="https://..." />
          </Form.Item>
          <Form.Item name="remarks" label="备注">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default MainlineDesignDocs;
